import { useFormik } from 'formik';
import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';

import CalendarIcon from '../../../assets/calendar-icon.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ToolTip } from 'src/commons/components';
import { ActionButton, InlineButton } from 'src/commons/components/Button';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import { DateDropDown } from 'src/commons/components/DateDropDown';
import Heading from 'src/commons/components/Heading';
import SubHeading from 'src/commons/components/SubHeading';
import {
  ApplicationStatus,
  ApplicationWorkflowEvent,
  CARDSTAR_OVERRIDE_ACCOUNT_CREATION,
  colors,
  MOBILE_BREAKPOINT,
  PageNames,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { GraphQLResponse } from 'src/commons/data/graphql';
import {
  AutoPayPreferencesPayload,
  createCreditCardApplicationAutopayPreferences,
  processCreditCardApplicationWorkflowEvent,
} from 'src/commons/data/queries';
import { ifFeatureFlagOn } from 'src/commons/utilities/feature-flags';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';
import { useAnnualFee } from 'src/commons/utilities/useAnnualFee';

interface FormValues {
  monthlyPaymentDay: string;
}

// #region -- Styles ------------------------------------------------------------------------------
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row-reverse nowrap;
  flex: 1;
  justify-content: center;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0;
    flex-flow: column;
    align-items: center;
  }
`;
Container.displayName = 'Container';

const IconContainer = styled.div`
  height: 2rem;
  width: 2rem;
`;
IconContainer.displayName = 'IconContainer';

const StyledCard = styled(Card)`
  height: 32.3125rem;
  min-height: unset;
  padding: 3.75rem 0 4.8125rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 1rem;
    padding: 3.75rem 1rem 4.8125rem;
  }
`;
StyledCard.displayName = 'StyledCard';

const HeadingContainer = styled.div`
  max-width: 15rem;
  margin: 1.4375rem 0 1.3125rem;
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
HeadingContainer.displayName = 'HeadingContainer';

const HeadingText = styled(Heading)`
  line-height: 2rem;
`;
HeadingText.displayName = 'HeadingText';

const SubHeadingContainer = styled.div`
  width: 100%;
  margin: 0 0 1.3125rem;
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
SubHeadingContainer.displayName = 'SubHeadingContainer';

const SubHeadingText = styled(SubHeading)`
  margin: 0;
  padding: 0 0.0625rem 0 0;
  line-height: 1.5rem;
`;
SubHeadingText.displayName = 'SubHeadingText';

const SubmitButton = styled(ActionButton)`
  margin: 1rem 0 0;
`;
SubmitButton.displayName = 'SubmitButton';

const DeclineButton = styled(InlineButton)`
  margin: 1.625rem auto 0;
  text-decoration: none;
  color: ${colors.TEXT_ANCHOR};
`;
DeclineButton.displayName = 'DeclineButton';
// #endregion
// #region -- Constants ---------------------------------------------------------------------------
const DROPDOWN_PLACEHOLDER = 'Select your monthly due date';
const id = 'payment-date'; // used to prefix id values in the render
const TOOLTIP_HEADING = 'Try AutoPay at any time';

// #endregion

export const PaymentDate = () => {
  const {
    creditCardApplication,
    fetchingApplication,
    fetchAndResumeCreditCardApplication,
  } = useContext(CreditCardApplicationContext);
  const { applicationStatus } = creditCardApplication;
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(false);
  const [submittingApp, setSubmittingApp] = useState(false);
  const annualFee = useAnnualFee();

  const TOOLTIP_BODY = `You can turn AutoPay on or off at any time, but the offer to waive your
  first year’s annual fee of $${annualFee} by enrolling in AutoPay expires before your first billing cycle.`;

  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.PAYMENT_DATE);
  }, []);

  const isPendingUnconditionalSubmission = () =>
    applicationStatus &&
    applicationStatus === ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION;

  const processWorkflowEventApplicationSubmitted = async () => {
    setSubmittingApp(true);
    try {
      const workflowEvent = ApplicationWorkflowEvent.APPLICATION_SUBMITTED;
      const applicationWorkflowEventAttributes = {
        onboardingOverride: ifFeatureFlagOn(CARDSTAR_OVERRIDE_ACCOUNT_CREATION),
      };
      const res = await processCreditCardApplicationWorkflowEvent(
        applicationId!,
        workflowEvent,
        applicationWorkflowEventAttributes
      );
      const { data } = res;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data?.processCreditCardApplicationWorkflowEvent) {
        navigate(
          prefixRoute(`/application/${applicationId}/export-waiting-room`)
        );
      } else {
        setError(true);
        setSubmittingApp(false);
      }
    } catch (err: any) {
      setSubmittingApp(false);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setError(true);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'PaymentDate',
          function: 'processWorkflowEventApplicationSubmitted',
        });
      }
    }
  };

  const handleSubmit = () => {
    if (isPendingUnconditionalSubmission()) {
      processWorkflowEventApplicationSubmitted();
    } else {
      navigate(
        prefixRoute(`/application/${applicationId}/export-waiting-room`)
      );
    }
  };

  const savePaymentDate = async (
    payload: AutoPayPreferencesPayload
  ): Promise<GraphQLResponse> => {
    const res = await createCreditCardApplicationAutopayPreferences(
      applicationId!,
      payload
    );
    if (!res?.data?.createCreditCardApplicationAutopayPreferences) {
      const err = new Error('Failed to save PaymentDate preferences');
      throw err;
    }
    return res;
  };

  const handleSkip = async () => {
    const payload = {
      paymentAmountType: 'NO_AUTOPAY',
    };
    setSubmittingApp(true);
    try {
      const res = await savePaymentDate(payload);
      if (!res?.data?.createCreditCardApplicationAutopayPreferences) {
        throw new Error('Failed to save PaymentDate preferences');
      }
      if (
        res.data.createCreditCardApplicationAutopayPreferences
          .paymentAmountType !== payload.paymentAmountType
      ) {
        throw new Error('Response does not match sent values');
      }
      handleSubmit();
    } catch (err: any) {
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        setError(true);
        setSubmittingApp(false);
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'PaymentDate',
          function: 'handleSkip',
        });
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      monthlyPaymentDay: '',
    },
    validationSchema: Yup.object().shape({
      monthlyPaymentDay: Yup.string(),
    }),
    onSubmit: async (values: FormValues, { setSubmitting }) => {
      const { monthlyPaymentDay } = values;
      const payload = {
        paymentAmountType: 'NO_AUTOPAY',
        monthlyPaymentDay: parseInt(monthlyPaymentDay, 10),
      };
      try {
        setSubmitting(true);
        await savePaymentDate(payload);
        handleSubmit();
      } catch (err: any) {
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          setError(true);
          setSubmitting(false);
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'PaymentDate',
            function: 'onSubmit',
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleSelectedPaymentDate = () => {
    const value = formik.values.monthlyPaymentDay;
    if (value !== '') {
      return `Payment date - ${value} of each month`;
    }
    return DROPDOWN_PLACEHOLDER;
  };

  // Render ---------------------------------------------------------------------------------------
  if (error) {
    return <ErrorCard />;
  }
  if (fetchingApplication) {
    return <LoadingCard />;
  }
  return (
    <Container>
      <StyledCard id={`${id}-card`}>
        <IconContainer id={`${id}-icon`}>
          <CalendarIcon aria-hidden />
        </IconContainer>
        <HeadingContainer>
          <HeadingText id={`${id}-heading`}>
            Would you like to select a due date?
          </HeadingText>
        </HeadingContainer>
        <SubHeadingContainer>
          <SubHeadingText id={`${id}-subheading`}>
            You can always turn on AutoPay later.
          </SubHeadingText>
          <ToolTip
            id={`${id}=tooltip`}
            heading={TOOLTIP_HEADING}
            body={TOOLTIP_BODY}
          />
        </SubHeadingContainer>
        <DateDropDown
          name="monthlyPaymentDay"
          value={formik.values.monthlyPaymentDay}
          label={DROPDOWN_PLACEHOLDER}
          onChange={formik.handleChange}
          formatSelection={handleSelectedPaymentDate}
          error={formik.errors.monthlyPaymentDay}
          touched={formik.touched.monthlyPaymentDay}
          disabled={formik.isSubmitting}
          onBlur={formik.handleBlur}
        />
        <SubmitButton
          id={`${id}-submit-button`}
          disabled={
            !formik.values.monthlyPaymentDay ||
            formik.isSubmitting ||
            submittingApp
          }
          onClick={() => formik.handleSubmit()}
          type="submit"
        >
          Save payment preferences
        </SubmitButton>
        <DeclineButton
          id={`${id}-decline-button`}
          onClick={handleSkip}
          type="button"
        >
          Skip and have a due date assigned to me
        </DeclineButton>
      </StyledCard>
    </Container>
  );
};
